import React, { useCallback, useMemo } from "react";
import {
    ActorType,
    AuditLogChangeType,
    AuditLogFilter,
    ResourceType,
} from "@common/types/auditLog";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import { FilterSectionType } from "./AuditLogFilters.lib";

type AuditLogFilterKeys = "resourceType" | "changeType" | "actorType";
type AuditLogFilterSubset = Pick<AuditLogFilter, AuditLogFilterKeys>;

const resourceTypes = new Set<string>(Object.values(ResourceType));
const changeTypes = new Set<string>(Object.values(AuditLogChangeType));
const actorTypes = new Set<string>(Object.values(ActorType));

const isResourceType = (value: string): value is ResourceType =>
    resourceTypes.has(value);
const isChangeType = (value: string): value is AuditLogChangeType =>
    changeTypes.has(value);
const isActorType = (value: string): value is ActorType =>
    actorTypes.has(value);

export interface FilterOption {
    id: string;
    label: string;
    type: string;
}

export interface FilterSection {
    key: string;
    title: string;
    type: FilterSectionType;
    filterKey: keyof AuditLogFilterSubset;
}

interface FilterChoice {
    filterKey: keyof AuditLogFilterSubset;
    optionId: string;
}

// Option ids of different filter keys may collide, so a row's value is scoped by its key.
const choiceValue = ({ filterKey, optionId }: FilterChoice) =>
    `${filterKey}:${optionId}`;

interface AuditLogFiltersProps {
    filters: AuditLogFilterSubset;
    onChange: (filters: AuditLogFilterSubset) => void;
    filterOptions: FilterOption[];
    filterSections: FilterSection[];
}

/**
 * Searchable audit log filter list: one titled pick-one section per filter
 * key, where picking a row replaces that section's choice and picking it again
 * clears it.
 */
export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
    filters,
    onChange,
    filterOptions,
    filterSections,
}) => {
    const { sections, choicesByValue } = useMemo(() => {
        const choices = new Map<string, FilterChoice>();
        const pickerSections: Array<PickerSectionData<string>> =
            filterSections.map((section) => ({
                key: section.key,
                title: section.title,
                exclusive: true,
                items: filterOptions
                    .filter((option) => option.type === section.type)
                    .map((option) => {
                        const choice = {
                            filterKey: section.filterKey,
                            optionId: option.id,
                        };
                        const value = choiceValue(choice);
                        choices.set(value, choice);
                        return { value, label: option.label };
                    }),
            }));
        return { sections: pickerSections, choicesByValue: choices };
    }, [filterOptions, filterSections]);

    const value = useMemo(
        () =>
            filterSections.flatMap((section) => {
                const optionId = filters[section.filterKey];
                return optionId
                    ? [choiceValue({ filterKey: section.filterKey, optionId })]
                    : [];
            }),
        [filterSections, filters],
    );

    const handleValueChange = useCallback(
        (nextValue: string[]) => {
            const nextFilters: AuditLogFilterSubset = {
                resourceType: undefined,
                changeType: undefined,
                actorType: undefined,
            };
            for (const choice of nextValue.flatMap(
                (choiceKey) => choicesByValue.get(choiceKey) ?? [],
            )) {
                const { filterKey, optionId } = choice;
                if (filterKey === "resourceType" && isResourceType(optionId)) {
                    nextFilters.resourceType = optionId;
                } else if (
                    filterKey === "changeType" &&
                    isChangeType(optionId)
                ) {
                    nextFilters.changeType = optionId;
                } else if (filterKey === "actorType" && isActorType(optionId)) {
                    nextFilters.actorType = optionId;
                }
            }
            onChange(nextFilters);
        },
        [choicesByValue, onChange],
    );

    const clearFilters = useCallback(() => {
        onChange({
            resourceType: undefined,
            changeType: undefined,
            actorType: undefined,
        });
    }, [onChange]);

    return (
        <FilterPickerList
            sections={sections}
            value={value}
            onValueChange={handleValueChange}
            emptyLabel="No matching filters"
            searchPlaceholder="Search filters..."
            onDeselect={clearFilters}
        />
    );
};
